/**
 * Shared types and helpers for the per-domain prism-new legacy maps (prism-new-legacy-maps*.ts).
 */

export interface LegacyContext {
  /** Query params the CRM sent. */
  params: Record<string, any>;
  /** Path segments of the call as the CRM made it (before any endpoint rename). */
  segments: string[];
  /** prism-new `meta.pagination`, when the endpoint returns one. */
  meta?: { page?: number; pageSize?: number; total?: number; totalPages?: number };
}

/** The legacy `data`, plus the legacy top-level `pagination` for endpoints whose old body had one. */
export interface LegacyPayload {
  data: unknown;
  pagination?: unknown;
}

export type LegacyMap = (data: any, ctx: LegacyContext) => LegacyPayload;

export interface PageInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export const rows = (value: unknown): any[] => (Array.isArray(value) ? value : []);

export function pageInfo(ctx: LegacyContext, source?: { page?: number; limit?: number; total?: number; totalPages?: number } | null, total?: number): PageInfo {
  const count = Number(source?.total ?? total ?? ctx.meta?.total ?? 0);
  const page = Number(source?.page ?? ctx.meta?.page ?? ctx.params['page'] ?? 1);
  const limit = Number(source?.limit ?? ctx.meta?.pageSize ?? ctx.params['limit'] ?? 0) || Math.max(count, 1);
  const totalPages = Number(source?.totalPages ?? ctx.meta?.totalPages ?? Math.ceil(count / limit));
  return { page, limit, total: count, totalPages, hasNext: page < totalPages, hasPrev: page > 1 };
}

/** `{ key: [...] }` → `[...]`; anything else is returned as it came. */
export const unwrap = (key: string): LegacyMap => (d) => ({ data: Array.isArray(d?.[key]) ? d[key] : d });

/** `key in d`, but false (not a TypeError) when the payload is not an object. */
export const has = (d: unknown, key: string) => !!d && typeof d === 'object' && key in d;
